/**
 * High-level patch creation for use with complex nested objects.
 */
import { createRef, createSubOid } from './oids.js';
import { diffToPatches, initialToPatches, } from './operation.js';
import { isRef } from './refs.js';
import { isObject } from './utils.js';
export class PatchCreator {
    constructor(getNow, createSubId) {
        this.getNow = getNow;
        this.createSubId = createSubId;
        this.isPrimitive = (value) => {
            return !isObject(value) || isRef(value);
        };
        this.createDiff = (from, to, options = {}) => {
            return diffToPatches(from, to, this.getNow, this.createSubId, [], options);
        };
        this.createInitialize = (obj, oid) => {
            return initialToPatches(obj, oid, this.getNow, this.createSubId);
        };
        this.createSet = (oid, key, value) => {
            // incoming value must be normalized. if it's not a primitive, it and all sub-objects
            // must be created
            if (this.isPrimitive(value)) {
                return [
                    {
                        oid,
                        timestamp: this.getNow(),
                        data: {
                            op: 'set',
                            name: key,
                            value,
                        },
                    },
                ];
            }
            else {
                const itemOid = createSubOid(oid, this.createSubId);
                return [
                    // since we're setting a complex nested object, we can initialize it wholesale.
                    // no diffing to do.
                    ...initialToPatches(value, itemOid, this.getNow),
                    // then set the reference to the object
                    {
                        oid,
                        timestamp: this.getNow(),
                        data: {
                            op: 'set',
                            value: createRef(itemOid),
                            name: key,
                        },
                    },
                ];
            }
        };
        this.createRemove = (oid, key) => {
            return [
                {
                    oid,
                    timestamp: this.getNow(),
                    data: {
                        op: 'remove',
                        name: key,
                    },
                },
            ];
        };
        this.createListPush = (oid, value) => {
            if (this.isPrimitive(value)) {
                return [
                    {
                        oid,
                        timestamp: this.getNow(),
                        data: {
                            op: 'list-push',
                            value,
                        },
                    },
                ];
            }
            else {
                const itemOid = createSubOid(oid, this.createSubId);
                return [
                    ...initialToPatches(value, itemOid, this.getNow),
                    {
                        oid,
                        timestamp: this.getNow(),
                        data: {
                            op: 'list-push',
                            value: createRef(itemOid),
                        },
                    },
                ];
            }
        };
        this.createListAdd = (oid, value) => {
            if (!this.isPrimitive(value)) {
                return [
                    {
                        oid,
                        timestamp: this.getNow(),
                        data: {
                            op: 'list-add',
                            value: createRef(value),
                        },
                    },
                ];
            }
            else {
                return [
                    {
                        oid,
                        timestamp: this.getNow(),
                        data: {
                            op: 'list-add',
                            value,
                        },
                    },
                ];
            }
        };
        this.createListInsert = (oid, index, value) => {
            if (this.isPrimitive(value)) {
                return [
                    {
                        oid,
                        timestamp: this.getNow(),
                        data: {
                            op: 'list-insert',
                            value,
                            index,
                        },
                    },
                ];
            }
            else {
                const itemOid = createSubOid(oid, this.createSubId);
                return [
                    ...initialToPatches(value, itemOid, this.getNow),
                    {
                        oid,
                        timestamp: this.getNow(),
                        data: {
                            op: 'list-insert',
                            value: createRef(itemOid),
                            index,
                        },
                    },
                ];
            }
        };
        this.createListRemove = (oid, value, only) => {
            return [
                {
                    oid,
                    timestamp: this.getNow(),
                    data: {
                        op: 'list-remove',
                        value,
                        only,
                    },
                },
            ];
        };
        this.createListDelete = (oid, index, count = 1) => {
            return [
                {
                    oid,
                    timestamp: this.getNow(),
                    data: {
                        op: 'list-delete',
                        index,
                        count,
                    },
                },
            ];
        };
        this.createListMoveByRef = (oid, value, index) => {
            return [
                {
                    oid,
                    timestamp: this.getNow(),
                    data: {
                        op: 'list-move-by-ref',
                        value,
                        index,
                    },
                },
            ];
        };
        this.createListMoveByIndex = (oid, fromIndex, toIndex) => {
            return [
                {
                    oid,
                    timestamp: this.getNow(),
                    data: {
                        op: 'list-move-by-index',
                        from: fromIndex,
                        to: toIndex,
                    },
                },
            ];
        };
        this.createDelete = (oid) => {
            return [
                {
                    oid,
                    timestamp: this.getNow(),
                    data: {
                        op: 'delete',
                    },
                },
            ];
        };
        this.createDeleteAll = (oids) => {
            return oids.map((oid) => ({
                oid,
                timestamp: this.getNow(),
                data: {
                    op: 'delete',
                },
            }));
        };
    }
}
//# sourceMappingURL=patch.js.map